import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge, Button, Card, Logo, Stack, StyledContainer, Text,
} from '@piwikpro/ui-components';
import { useNavigate } from '../../../hooks/useNavigate';
import { App } from '../../../model';
import styles from './SuccessView.module.css';
import successImage from './SuccessImage.png';

interface AppCreatorSuccessProps {
  app: App
  isWebsiteApp: boolean
  showLogo: boolean
}

const SuccessView = ({
  app,
  isWebsiteApp,
  showLogo,
}: AppCreatorSuccessProps) => {
  const { t } = useTranslation(['administration']);
  const navigate = useNavigate();

  return (
    <StyledContainer backgroundImage="square-dot-gradient">
      <div style={{
        width: '660px', margin: '0 auto', padding: '32px 0', height: 'calc(100vh - 64px)',
      }}
      >
        <Stack vertical horizontalAlignment="middle">
          {showLogo && (
            <Stack.Item>
              <div data-ppuc-id="ProductBar.Logo" className={styles.logo}>
                <Logo height={20} />
              </div>
            </Stack.Item>
          )}
          <Stack.Item>
            <img src={successImage} alt="Success illustration" className={styles.illustration} />
          </Stack.Item>
          <Stack.Item>
            <Text as="h1" size={24} weight={700} wrapType="noWrapAndCutText" dataId="Header.large">{t('apps.success.greatJobTitle')}</Text>
          </Stack.Item>
          <Stack.Item>
            <Text as="p" color="InkLighter" align="center">{t('apps.success.completedSetupSubtitle')}</Text>
          </Stack.Item>
          <Stack.Item fill previousSpacing="extraWide">
            <>
              <div className={styles.cardPaddingsOverwrite}>
                <Card connected>
                  <Card.Section subdued>
                    <Stack verticalAlignment="center">
                      <Stack.Item>
                        <Badge
                          iconLeft={{ name: 'checkmark', color: 'green', size: 'default' }}
                          color="light-green"
                          size="large"
                        />
                      </Stack.Item>
                      <Stack.Item fill>
                        <Stack vertical spacing="extraNarrow">
                          <Stack.Item>
                            <Stack verticalAlignment="center" spacing="narrow">
                              <Stack.Item>
                                <Text as="p" color="InkLightest" weight={600} dataId="Label" wrapType="wrap">{t('apps.success.setupStep')}</Text>
                              </Stack.Item>
                              <Stack.Item>
                                <Badge color="light-green" size="medium" text={app.attributes.name} />
                              </Stack.Item>
                            </Stack>
                          </Stack.Item>
                        </Stack>
                      </Stack.Item>
                      <Stack.Item>
                        <Button
                          text={t('apps.success.goToYourApp')}
                          onClick={() => navigate({
                            to: isWebsiteApp
                              ? '/administration/apps/$appId/installation/sites'
                              : '/administration/apps/$appId/installation/mobile-apps',
                            params: { appId: app.id },
                          })}
                          fullWidth
                        />
                      </Stack.Item>
                    </Stack>
                  </Card.Section>
                </Card>
              </div>
              <Card connected>
                <Card.Section>
                  <Stack verticalAlignment="center">
                    <Stack.Item>
                      <Badge iconLeft={{ name: 'debug', color: 'blue', size: 'default' }} color="light-blue" size="large" />
                    </Stack.Item>
                    <Stack.Item fill>
                      <Stack vertical spacing="extraNarrow">
                        <Stack.Item>
                          <Text as="p" weight={600} dataId="Label">{t('apps.success.checkTrackingStep')}</Text>
                        </Stack.Item>
                        <Stack.Item>
                          <Text as="p" size={12} dataId="Caption" color="InkLighter">{t('apps.success.checkTrackingDescription')}</Text>
                        </Stack.Item>
                      </Stack>
                    </Stack.Item>
                    <Stack.Item>
                      <Text as="p" size={12} dataId="Caption" color="InkLighter">{t('apps.success.timeEstimateOneMinute')}</Text>
                    </Stack.Item>
                  </Stack>
                </Card.Section>
              </Card>
              <Card>
                <Button
                  appearance="constructive"
                  iconRight="arrow-right"
                  text={t('apps.success.goToTrackerDebugger')}
                  onClick={() => { window.location.href = `/analytics/#/${app.id}/settings/debugger/`; }}
                  fullWidth
                />
              </Card>
            </>
          </Stack.Item>
        </Stack>
      </div>
    </StyledContainer>
  );
};

export default SuccessView;
